import countryToCurrency from 'country-to-currency';
import type { CurrencyOption } from 'components/ui/organisms/currency-selection-modal';

/**
 * Non-USD currencies checkout can settle payment in. Mirrors the backend
 * SUPPORTED_PAY_IN_CURRENCIES ceiling (minus USD) confirmed by the Stripe
 * settlement spike. Also gates the Thank You "charged in <local>" display.
 */
export const SUPPORTED_LOCAL_CURRENCIES = [
  'INR',
  'EUR',
  'GBP',
  'BRL',
  'MXN',
  'AUD',
  'CAD',
  'CHF',
  'CNY',
  'JPY',
  'KRW',
  'SGD',
  'ZAR',
] as const;

export type SupportedLocalCurrency =
  (typeof SUPPORTED_LOCAL_CURRENCIES)[number];

export const DEFAULT_CURRENCY = 'USD';

/**
 * Technical ceiling of currencies checkout can settle payment in (USD + the
 * local set). This is the whitelist Monarch is allowed to enable from — it
 * mirrors the backend SUPPORTED_PAY_IN_CURRENCIES confirmed by the Stripe
 * settlement spike. It is NOT the default: see DEFAULT_ACCEPTED_PAY_IN_CURRENCIES.
 */
export const ACCEPTED_PAY_IN_CURRENCIES: readonly string[] = [
  DEFAULT_CURRENCY,
  ...SUPPORTED_LOCAL_CURRENCIES,
];

/**
 * Conservative default pay-in set (the MVP currencies already live in
 * production). Used as the safe fallback when Monarch does not supply a usable
 * acceptedCurrencies list, so the additional Phase-1 currencies only settle in
 * local currency once they are explicitly turned on in Monarch.
 */
export const DEFAULT_ACCEPTED_PAY_IN_CURRENCIES: readonly string[] = [
  DEFAULT_CURRENCY,
  'INR',
  'EUR',
  'GBP',
  'BRL',
  'MXN',
];

/**
 * Resolves the effective set of pay-in (settleable) currencies for checkout.
 * Prefers the Monarch-configured list so a currency can be turned on/off
 * without a code deploy, but only honors codes checkout can technically settle
 * (filtered against ACCEPTED_PAY_IN_CURRENCIES, guarding against typos /
 * currencies with no backend support). USD is always kept settleable when
 * Monarch supplies a list, since it is the settlement base/fallback. Falls back
 * to the conservative MVP set (DEFAULT_ACCEPTED_PAY_IN_CURRENCIES) when Monarch
 * provides nothing usable.
 */
export const resolveAcceptedCurrencies = (
  monarchAcceptedCurrencies?: string[] | null,
): readonly string[] => {
  const configured = Array.from(
    new Set(
      (monarchAcceptedCurrencies ?? [])
        .map((code) => code?.toUpperCase())
        .filter((code) => ACCEPTED_PAY_IN_CURRENCIES.includes(code)),
    ),
  );

  if (configured.length === 0) {
    return DEFAULT_ACCEPTED_PAY_IN_CURRENCIES;
  }

  return configured.includes(DEFAULT_CURRENCY)
    ? configured
    : [DEFAULT_CURRENCY, ...configured];
};

/** Whether checkout may settle payment in the given currency. */
export const isCurrencyAcceptedForPayment = (
  code: string | undefined | null,
  acceptedCurrencies: readonly string[],
): boolean => !!code && acceptedCurrencies.includes(code.toUpperCase());

type ResolveInitialCurrencyParams = {
  /** Currency the shopper explicitly chose upstream (edx_user_currency). */
  explicitCurrency?: string | null;
  /** ISO country code derived from the geo cookie. */
  countryCode?: string | null;
  /** Set the resolved currency must belong to (pre-selectable/display set). */
  acceptedCurrencies?: readonly string[];
  /** Whether IP/geo pre-selection is turned on for checkout. */
  isIpDetectionEnabled?: boolean;
  /** Currency used when nothing else resolves (BAU). */
  fallbackCurrency?: string;
};

/** Upper-cases a currency/country code, or returns an empty string when blank. */
const normalizeCode = (code?: string | null): string =>
  code ? code.toUpperCase() : '';

/** Whether a currency code is in the accepted (settleable) list. */
const isAcceptedCurrency = (
  code: string | undefined,
  acceptedCurrencies: readonly string[],
): boolean => !!code && acceptedCurrencies.includes(code);

/**
 * Currency mapped to the shopper's country, or undefined when unmapped. Uses
 * the maintained `country-to-currency` package (ISO 3166-1 alpha-2 → ISO 4217)
 * as the source of truth instead of a hand-maintained table, so Eurozone
 * membership and other changes stay current without code edits. The resolved
 * currency is still gated by the accepted/display set upstream.
 */
const resolveGeoCurrency = (countryCode?: string | null): string | undefined =>
  countryToCurrency[
    normalizeCode(countryCode) as keyof typeof countryToCurrency
  ];

/**
 * Resolves the currency checkout should start on, in priority order:
 *   1. An explicit upstream selection (always wins when accepted).
 *   2. The IP/geo-detected currency for the shopper's country (only when the
 *      detection toggle is on and the mapped currency is accepted).
 *   3. The fallback currency (USD / BAU).
 *
 * Candidates are evaluated in order and the first accepted one wins, so adding
 * a new signal is just a matter of inserting another entry. Pure and
 * side-effect free so it can be unit tested in isolation.
 */
export const resolveInitialCurrency = ({
  explicitCurrency,
  countryCode,
  acceptedCurrencies = ACCEPTED_PAY_IN_CURRENCIES,
  isIpDetectionEnabled = false,
  fallbackCurrency = DEFAULT_CURRENCY,
}: ResolveInitialCurrencyParams): string => {
  const candidates = [
    normalizeCode(explicitCurrency),
    isIpDetectionEnabled ? resolveGeoCurrency(countryCode) : undefined,
  ];

  const resolved = candidates.find((code) =>
    isAcceptedCurrency(code, acceptedCurrencies),
  );

  return resolved ?? fallbackCurrency;
};

export const currencyOptions: CurrencyOption[] = [
  { code: 'AUD', flag: '🇦🇺', name: 'Australian Dollar', symbol: '$' },
  { code: 'BRL', flag: '🇧🇷', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'CAD', flag: '🇨🇦', name: 'Canadian Dollar', symbol: '$' },
  { code: 'CHF', flag: '🇨🇭', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'CNY', flag: '🇨🇳', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'EUR', flag: '🇪🇺', name: 'Euro', symbol: '€' },
  { code: 'GBP', flag: '🇬🇧', name: 'British Pound', symbol: '£' },
  { code: 'INR', flag: '🇮🇳', name: 'Indian Rupee', symbol: '₹' },
  { code: 'JPY', flag: '🇯🇵', name: 'Japanese Yen', symbol: '¥' },
  { code: 'KRW', flag: '🇰🇷', name: 'South Korean Won', symbol: '₩' },
  { code: 'MXN', flag: '🇲🇽', name: 'Mexican Peso', symbol: '$' },
  { code: 'SGD', flag: '🇸🇬', name: 'Singapore Dollar', symbol: '$' },
  { code: 'USD', flag: '🇺🇸', name: 'US Dollar', symbol: '$' },
  { code: 'ZAR', flag: '🇿🇦', name: 'South African Rand', symbol: 'R' },
];

/** Lookup a selector option by code, falling back to USD when unknown/missing. */
export const resolveCurrencyOption = (code?: string | null): CurrencyOption => {
  const normalized = code?.toUpperCase();
  return (
    currencyOptions.find((option) => option.code === normalized) ??
    currencyOptions.find((option) => option.code === DEFAULT_CURRENCY)!
  );
};

/**
 * Currencies checkout can display/pre-select (the full selector set). Broader
 * than ACCEPTED_PAY_IN_CURRENCIES: any of these can be pre-selected for a
 * reference price, while settlement stays USD unless the backend has a locked
 * pay-in quote for the currency.
 */
export const DISPLAY_CURRENCIES: readonly string[] = currencyOptions.map(
  (option) => option.code,
);
